// src/components/layout/CustomSider.tsx - Reviewed for impersonation support
import React, { useState, useEffect } from 'react';
import { Layout, Menu, Typography, Drawer, Button } from 'antd';
import {
  DashboardOutlined,
  VideoCameraOutlined,
  QuestionCircleOutlined,
  MenuOutlined,
  CloseOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetIdentity } from '@refinedev/core';

const { Sider } = Layout;
const { Text } = Typography;

export const CustomSider: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { data: user } = useGetIdentity<any>();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'New Inspection Review',
    },
    {
      key: '/videos',
      icon: <VideoCameraOutlined />,
      label: 'My Reviews',
    },
    {
      key: '/help-support',
      icon: <QuestionCircleOutlined />,
      label: 'Help & Support',
    },
  ];

  // Show admin menu for actual admins (not impersonated users who might be admins)
  // This ensures admins can't see admin menu when impersonating another admin
  if (user?.isAdmin && !user?.isImpersonated) {
    menuItems.push({
      key: '/admin',
      icon: <SettingOutlined />,
      label: 'Admin',
    });
  }

  const handleMenuClick = ({ key }: { key: string }) => {
    // If already on dashboard, force reload to reset state for new review
    if (key === '/dashboard' && location.pathname === '/dashboard') {
      window.location.reload();
      return;
    }
    navigate(key);
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const siderContent = (
    <>
      {/* Spectreviews Logo/Brand */}
      <div
        style={{
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          padding: '0 20px',
          borderBottom: '1px solid #f0f0f0',
          background: '#fafafa',
          cursor: 'pointer',
        }}
        onClick={() => {
          navigate('/dashboard');
          if (isMobile) setMobileMenuOpen(false);
        }}
      >
        {/* Spectreviews Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Logo Icon */}
          <div>
            <img src="/specticon.png" width="32" height="32" alt="Spectreviews" />
          </div>
          
          {/* Spectreviews Text */}
          <Text 
            strong 
            style={{ 
              fontSize: '18px', 
              color: '#1f2937',
              fontWeight: '600',
              letterSpacing: '-0.025em'
            }}
          >
            Spectreviews
          </Text>
        </div>
      </div>

      {/* Navigation Menu */}
      <Menu
        mode="inline"
        selectedKeys={[location.pathname]}
        onClick={handleMenuClick}
        items={menuItems}
        style={{
          border: 'none',
          marginTop: '12px',
          padding: '0 8px',
          fontWeight: 500,
          fontSize: '14px',
        }}
      />
      <style>{`
        .ant-menu-inline .ant-menu-item {
          border-radius: 8px !important;
          margin: 2px 0 !important;
          height: 42px !important;
          line-height: 42px !important;
        }
        .ant-menu-inline .ant-menu-item-selected {
          background-color: #eff6ff !important;
          color: #2563eb !important;
        }
        .ant-menu-inline .ant-menu-item-selected::after {
          display: none !important;
        }
        .ant-menu-inline .ant-menu-item:not(.ant-menu-item-selected):hover {
          background-color: #f9fafb !important;
        }
        .ant-menu-inline .ant-menu-item .anticon {
          font-size: 16px !important;
        }
      `}</style>

      {/* Impersonation indicator in sidebar (mobile only) */}
      {isMobile && user?.isImpersonated && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '12px 20px',
          borderTop: '1px solid #f0f0f0',
          background: '#fff8e1',
          fontSize: '12px',
          color: '#f57c00',
          textAlign: 'center'
        }}>
          Viewing as: {user.email}
        </div>
      )}
    </>
  );

  // Mobile view - show hamburger menu button and drawer
  if (isMobile) {
    return (
      <>
        <Button
          className="mobile-menu-trigger"
          icon={<MenuOutlined />}
          onClick={() => setMobileMenuOpen(true)}
          style={{
            position: 'fixed',
            left: 16,
            top: 16,
            zIndex: 1001,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        />
        <Drawer
          placement="left"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          width={256}
          bodyStyle={{ padding: 0 }}
          closeIcon={<CloseOutlined />}
          style={{
            boxShadow: '2px 0 6px rgba(0,21,41,0.08)',
          }}
        >
          {siderContent}
        </Drawer>
      </>
    );
  }

  // Desktop view - show regular sider
  return (
    <Sider
      width={256}
      theme="light"
      style={{
        boxShadow: '2px 0 6px rgba(0,21,41,0.08)',
        borderRight: '1px solid #f0f0f0',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 100,
      }}
    >
      {siderContent}
    </Sider>
  );
};